import api from './api';
import { MediaFile, MediaType, SocialPlatform, PLATFORM_CONFIGS } from '../types/contentHub';

const BASE_URL = '/media';

export interface UploadProgress {
  loaded: number;
  total: number;
  percentage: number;
}

export interface UploadOptions {
  onProgress?: (progress: UploadProgress) => void;
  resize?: {
    width: number;
    height: number;
    fit?: 'cover' | 'contain' | 'fill';
  };
  optimize?: boolean;
  generateThumbnail?: boolean;
}

export interface ImageCropData {
  x: number;
  y: number;
  width: number;
  height: number;
  rotate?: number;
}

export const mediaService = {
  // Upload a single file
  async uploadFile(
    file: File,
    projectId: string,
    options?: UploadOptions
  ): Promise<MediaFile> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('projectId', projectId);

    if (options?.resize) {
      formData.append('resize', JSON.stringify(options.resize));
    }
    if (options?.optimize !== undefined) {
      formData.append('optimize', String(options.optimize));
    }
    if (options?.generateThumbnail !== undefined) {
      formData.append('generateThumbnail', String(options.generateThumbnail));
    }

    const response = await api.post(`${BASE_URL}/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      onUploadProgress: (progressEvent) => {
        if (options?.onProgress && progressEvent.total) {
          options.onProgress({
            loaded: progressEvent.loaded,
            total: progressEvent.total,
            percentage: Math.round((progressEvent.loaded * 100) / progressEvent.total)
          });
        }
      }
    });

    return response.data;
  },

  // Upload multiple files
  async uploadFiles(
    files: File[],
    projectId: string,
    options?: UploadOptions
  ): Promise<MediaFile[]> {
    const uploadPromises = files.map(file => this.uploadFile(file, projectId, options));
    return Promise.all(uploadPromises);
  },

  // Upload from URL
  async uploadFromUrl(
    url: string,
    projectId: string,
    fileName?: string
  ): Promise<MediaFile> {
    const response = await api.post(`${BASE_URL}/upload-url`, {
      url,
      projectId,
      fileName
    });
    return response.data;
  },

  // Get media file info
  async getMediaFile(mediaId: string): Promise<MediaFile> {
    const response = await api.get(`${BASE_URL}/${mediaId}`);
    return response.data;
  },

  // Delete media file
  async deleteMediaFile(mediaId: string): Promise<void> {
    await api.delete(`${BASE_URL}/${mediaId}`);
  },

  // Crop image
  async cropImage(
    mediaId: string,
    cropData: ImageCropData
  ): Promise<MediaFile> {
    const response = await api.post(`${BASE_URL}/${mediaId}/crop`, cropData);
    return response.data;
  },

  // Resize image
  async resizeImage(
    mediaId: string,
    width: number,
    height: number,
    fit: 'cover' | 'contain' | 'fill' = 'cover'
  ): Promise<MediaFile> {
    const response = await api.post(`${BASE_URL}/${mediaId}/resize`, {
      width,
      height,
      fit
    });
    return response.data;
  },

  // Create optimized versions for platforms
  async createPlatformVersions(
    mediaId: string,
    platforms: SocialPlatform[]
  ): Promise<Record<SocialPlatform, MediaFile>> {
    const response = await api.post(`${BASE_URL}/${mediaId}/platform-versions`, {
      platforms
    });
    return response.data;
  },

  // Generate thumbnail from video
  async generateVideoThumbnail(
    mediaId: string,
    timestamp?: number
  ): Promise<{ thumbnailUrl: string }> {
    const response = await api.post(`${BASE_URL}/${mediaId}/thumbnail`, {
      timestamp
    });
    return response.data;
  },

  // Get video metadata
  async getVideoMetadata(mediaId: string): Promise<{
    duration: number;
    width: number;
    height: number;
    fps: number;
    codec: string;
    bitrate: number;
  }> {
    const response = await api.get(`${BASE_URL}/${mediaId}/metadata`);
    return response.data;
  },

  // Trim video
  async trimVideo(
    mediaId: string,
    startTime: number,
    endTime: number
  ): Promise<MediaFile> {
    const response = await api.post(`${BASE_URL}/${mediaId}/trim`, {
      startTime,
      endTime
    });
    return response.data;
  },

  // Get presigned upload URL (for large files)
  async getPresignedUploadUrl(
    fileName: string,
    contentType: string,
    projectId: string
  ): Promise<{
    uploadUrl: string;
    fileKey: string;
    expiresAt: string;
  }> {
    const response = await api.post(`${BASE_URL}/presigned-url`, {
      fileName,
      contentType,
      projectId
    });
    return response.data;
  },

  // Confirm presigned upload completed
  async confirmPresignedUpload(
    fileKey: string,
    projectId: string
  ): Promise<MediaFile> {
    const response = await api.post(`${BASE_URL}/confirm-upload`, {
      fileKey,
      projectId
    });
    return response.data;
  },

  // Helper: Validate file before upload
  validateFile(
    file: File,
    platform: SocialPlatform,
    mediaType: MediaType
  ): { valid: boolean; errors: string[] } {
    const config = PLATFORM_CONFIGS[platform];
    const errors: string[] = [];

    // Check file size
    const maxSizeMB = config.mediaConstraints.maxFileSize;
    const fileSizeMB = file.size / (1024 * 1024);
    if (fileSizeMB > maxSizeMB) {
      errors.push(`File size exceeds ${maxSizeMB}MB limit for ${config.name}`);
    }

    // Check file format
    const extension = file.name.split('.').pop()?.toLowerCase() || '';
    if (mediaType === 'image') {
      if (!config.mediaConstraints.supportedImageFormats.includes(extension)) {
        errors.push(`${extension.toUpperCase()} images not supported on ${config.name}`);
      }
    } else if (mediaType === 'video') {
      if (!config.mediaConstraints.supportedVideoFormats.includes(extension)) {
        errors.push(`${extension.toUpperCase()} videos not supported on ${config.name}`);
      }
    }

    return { valid: errors.length === 0, errors };
  },

  // Helper: Get media type from file
  getMediaType(file: File): MediaType {
    const mimeType = file.type.toLowerCase();
    if (mimeType.startsWith('image/gif')) return 'gif';
    if (mimeType.startsWith('image/')) return 'image';
    if (mimeType.startsWith('video/')) return 'video';
    return 'image'; // default
  },

  // Helper: Create object URL for preview
  createPreviewUrl(file: File): string {
    return URL.createObjectURL(file);
  },

  // Helper: Revoke object URL
  revokePreviewUrl(url: string): void {
    URL.revokeObjectURL(url);
  },

  // Helper: Get recommended dimensions for platform
  getRecommendedDimensions(
    platform: SocialPlatform,
    aspectRatioValue?: string
  ): { width: number; height: number } | null {
    const config = PLATFORM_CONFIGS[platform];
    const ratios = config.mediaConstraints.aspectRatios;

    if (aspectRatioValue) {
      const ratio = ratios.find(r => r.value === aspectRatioValue);
      if (ratio) return { width: ratio.width, height: ratio.height };
    }

    // Return first (default) ratio
    if (ratios.length > 0) {
      return { width: ratios[0].width, height: ratios[0].height };
    }

    return null;
  },

  // Helper: Check if file needs processing
  needsProcessing(
    file: File,
    platform: SocialPlatform
  ): boolean {
    const config = PLATFORM_CONFIGS[platform];
    const fileSizeMB = file.size / (1024 * 1024);

    // Large files should be processed
    if (fileSizeMB > 10) return true;

    // Images can often be optimized
    if (file.type.startsWith('image/')) return true;

    return false;
  }
};

export default mediaService;
